import React from "react";
import {
  FiMapPin,
  FiMessageSquare,
  FiTrash2,
  FiEye,
  FiCalendar,
  FiUser,
  FiImage,
  FiMaximize2,
  FiHome,
} from "react-icons/fi";
import { formatCurrency, formatDate, resolveAssetUrl, stripHtml } from "../utils/formatters";
import "./PropertyCard.css";

const PropertyCard = ({
  property,
  onViewDetails,
  onOpenComments,
  onDelete,
  canDelete = false,
}) => {
  const {
    id,
    project_name,
    title,
    property_name,
    description,
    property_description,
    cost,
    expected_cost,
    starting_cost,
    price,
    city_name,
    state_name,
    area_name,
    property_type,
    property_purpose,
    property_status,
    property_stage,
    bhk,
    builtup_area,
    carpet_area,
    partner,
    customer,
    user,
    created_on,
    created_at,
  } = property || {};

  const propImagesList = Array.isArray(property?.propertyImages) ? property.propertyImages : [];
  const standardImagesList = Array.isArray(property?.images) ? property.images : [];
  const allImages = propImagesList.length > 0 ? propImagesList : standardImagesList;
  const firstImage = allImages.length > 0 ? allImages[0] : null;
  const imageUrl = firstImage?.image_url || firstImage?.imagePath || firstImage?.url || "";
  const resolvedImg = imageUrl ? resolveAssetUrl(imageUrl) : null;

  const propertyTitle = project_name || property_name || title || `Property #${id}`;
  const propertyCost = cost || expected_cost || starting_cost || price;
  const formattedPrice = formatCurrency(propertyCost);

  const propertyTypeName =
    typeof property_type === "object" ? property_type?.name : property_type;

  const purposeName =
    typeof property_purpose === "object" ? property_purpose?.name : property_purpose;

  const stageName =
    typeof property_stage === "object"
      ? property_stage?.name
      : typeof property_status === "object"
      ? property_status?.name
      : property_stage || property_status;

  const ownerName =
    partner?.first_name || customer?.first_name || user?.first_name || property?.customer_name
      ? `${partner?.first_name || customer?.first_name || user?.first_name || property?.customer_name || ""} ${
          partner?.last_name || customer?.last_name || user?.last_name || ""
        }`.trim()
      : "Partner / Agent";

  const rawDesc = property_description || description;
  const descriptionSnippet = stripHtml(rawDesc);
  const postDate = created_on || created_at;

  const areaSize = builtup_area || carpet_area;

  return (
    <div className="shared-property-card">
      {/* Thumbnail area */}
      <div
        className="shared-property-card__media"
        onClick={() => onViewDetails(property)}
      >
        {resolvedImg ? (
          <img
            src={resolvedImg}
            alt={propertyTitle}
            className="shared-property-card__img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("shared-property-card__media--fallback");
            }}
          />
        ) : (
          <div className="shared-property-card__placeholder">
            <FiImage size={36} />
            <span>Property Listing</span>
          </div>
        )}

        <div className="shared-property-card__badges">
          {propertyTypeName && (
            <span className="shared-property-card__type-badge">
              <FiHome size={12} />
              <span>{propertyTypeName}</span>
            </span>
          )}
          {purposeName && (
            <span className="shared-property-card__purpose-badge">
              {purposeName}
            </span>
          )}
          {stageName && (
            <span className="shared-property-card__stage-badge">
              {stageName}
            </span>
          )}
        </div>

        {formattedPrice && (
          <div className="shared-property-card__price-badge">
            <span className="shared-property-card__price-label">Price</span>
            <span className="shared-property-card__price-val">{formattedPrice}</span>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="shared-property-card__body">
        <h3
          className="shared-property-card__title"
          onClick={() => onViewDetails(property)}
          title={propertyTitle}
        >
          {propertyTitle}
        </h3>

        {/* Location info */}
        <div className="shared-property-card__location">
          <FiMapPin className="shared-property-card__loc-icon" />
          <span>
            {[area_name, city_name, state_name].filter(Boolean).join(", ") ||
              "Location Not Specified"}
          </span>
        </div>

        {/* Specs Pill row (BHK, Area) */}
        {(bhk || areaSize) && (
          <div className="shared-property-card__features-row">
            {bhk && (
              <span className="shared-property-card__feature-chip">
                {bhk} BHK
              </span>
            )}
            {areaSize && (
              <span className="shared-property-card__feature-chip">
                <FiMaximize2 size={11} /> {areaSize} sq.ft
              </span>
            )}
          </div>
        )}

        {/* Description snippet */}
        {descriptionSnippet && (
          <p className="shared-property-card__description">
            {descriptionSnippet.length > 120
              ? `${descriptionSnippet.slice(0, 120)}...`
              : descriptionSnippet}
          </p>
        )}

        {/* Creator / date metadata */}
        <div className="shared-property-card__meta">
          <div className="shared-property-card__author" title={`Listed by ${ownerName}`}>
            <FiUser className="shared-property-card__meta-icon" />
            <span className="shared-property-card__author-name">{ownerName}</span>
          </div>
          {postDate && (
            <div className="shared-property-card__date" title={`Listed on ${formatDate(postDate)}`}>
              <FiCalendar className="shared-property-card__meta-icon" />
              <span>{formatDate(postDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action footer */}
      <div className="shared-property-card__footer">
        <div className="shared-property-card__footer-left">
          <button
            type="button"
            className="shared-property-card__btn shared-property-card__btn--primary"
            onClick={() => onViewDetails(property)}
          >
            <FiEye size={14} />
            <span>Details</span>
          </button>

          <button
            type="button"
            className="shared-property-card__btn shared-property-card__btn--secondary"
            onClick={() => onOpenComments(property)}
            title="Discussion & Inquiries"
          >
            <FiMessageSquare size={14} />
            <span>Discussion</span>
          </button>
        </div>

        {canDelete && (
          <div className="shared-property-card__footer-right">
            <button
              type="button"
              className="shared-property-card__btn shared-property-card__btn--danger"
              onClick={() => onDelete(property)}
              title="Delete Property"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
